import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Truck, ShieldCheck, HeartHandshake, Flame } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1605292356183-a77d0a9c9d1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHx0cmFkaXRpb25hbCUyMGluZGlhbiUyMGRpeWElMjBsYW1wJTIwbGl0JTIwZGl3YWxpfGVufDB8fHx8MTc4NTEyNDU3Nnww&ixlib=rb-4.1.0&q=85";
const BRASS_IMG = "https://images.unsplash.com/photo-1755452540355-b0797e93ea03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBoYW5kaWNyYWZ0JTIwYnJhc3MlMjBwb3R0ZXJ5JTIwcHJvZHVjdHxlbnwwfHx8fDE3ODUxMjQ1NzZ8MA&ixlib=rb-4.1.0&q=85";
const LAMPS_IMG = "https://images.unsplash.com/photo-1759607236409-1df137ecb3b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjBoYW5kaWNyYWZ0JTIwYnJhc3MlMjBwb3R0ZXJ5JTIwcHJvZHVjdHxlbnwwfHx8fDE3ODUxMjQ1NzZ8MA&ixlib=rb-4.1.0&q=85";
const MARIGOLD_IMG = "https://images.unsplash.com/photo-1699764681875-dd04ce36b1c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxtYXJpZ29sZCUyMGZsb3dlciUyMGRlY29yYXRpb24lMjB0cmFkaXRpb25hbHxlbnwwfHx8fDE3ODUxMjQ1NzZ8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [p, b] = await Promise.all([
          api.get("/products", { params: { featured: true } }),
          api.get("/banners"),
        ]);
        setProducts(p.data.slice(0, 8));
        setBanners(b.data);
      } catch (e) { /* silent */ }
    })();
  }, []);

  function handleAdd(p) {
    addItem(p, 1);
    toast.success(`${p.name} added to cart`);
  }

  const activeBanner = banners[0];

  return (
    <div>
      {/* HERO with bento layout */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7 relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#4A148C] to-[#2C0855] p-10 lg:p-14 min-h-[420px] shadow-warm grain">
            <div className="relative z-10 max-w-xl">
              <div className="inline-flex items-center gap-2 bg-[#FFB300]/15 text-[#FFCF52] text-[11px] tracking-[0.25em] uppercase px-4 py-2 rounded-full mb-6">
                <Flame className="w-3.5 h-3.5 diya-flame" />
                {activeBanner?.festival || "Diwali"} Collection · 2026
              </div>
              <h1 className="font-display text-5xl lg:text-6xl leading-[1.05] text-[#FFF9F2] font-semibold">
                {activeBanner?.title || (
                  <>Light up every corner with <span className="text-[#FFB300]">handcrafted</span> tradition.</>
                )}
              </h1>
              <p className="font-body text-[#FFCF52]/90 text-lg mt-6 leading-relaxed">
                {activeBanner?.subtitle || "Diyas, brassware, textiles, and rangoli — sourced from artisan families across India, delivered to your doorstep in time for the celebrations."}
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Button
                  data-testid="hero-shop-btn"
                  onClick={() => navigate(activeBanner?.cta_link || "/shop")}
                  className="rounded-full bg-[#E65C00] hover:bg-[#C44A00] text-white px-8 py-6 text-base btn-lift"
                >
                  {activeBanner?.cta_text || "Shop the Collection"} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  data-testid="hero-featured-btn"
                  onClick={() => navigate("/shop?featured=1")}
                  variant="outline"
                  className="rounded-full border-[#FFCF52]/40 text-[#FFCF52] hover:bg-[#FFCF52]/10 hover:text-[#FFCF52] px-8 py-6 text-base"
                >
                  Festive Picks
                </Button>
              </div>
            </div>
            <img src={HERO_IMG} alt="Diyas" className="hidden lg:block absolute -right-10 -bottom-10 w-[440px] h-[440px] object-cover rounded-full opacity-90 border-8 border-[#FFB300]/30" />
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-5">
            <button onClick={() => navigate("/shop?category=Diyas")} className="col-span-2 relative overflow-hidden rounded-3xl aspect-[16/9] zoom-parent border border-[#FFD1A6] shadow-warm text-left">
              <img src={LAMPS_IMG} alt="Diyas" className="absolute inset-0 w-full h-full object-cover zoom-img" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/20 to-transparent" />
              <div className="relative p-6 h-full flex flex-col justify-end text-white">
                <div className="text-[10px] uppercase tracking-widest text-[#FFCF52]">Category</div>
                <div className="font-display text-3xl">Diyas & Lamps</div>
              </div>
            </button>
            <button onClick={() => navigate("/shop?category=Brass")} className="relative overflow-hidden rounded-3xl aspect-square zoom-parent border border-[#FFD1A6] shadow-warm text-left">
              <img src={BRASS_IMG} alt="Brass" className="absolute inset-0 w-full h-full object-cover zoom-img" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="relative p-5 h-full flex flex-col justify-end text-white">
                <div className="text-[10px] uppercase tracking-widest text-[#FFCF52]">Category</div>
                <div className="font-display text-2xl">Brassware</div>
              </div>
            </button>
            <button onClick={() => navigate("/shop?category=Decor")} className="relative overflow-hidden rounded-3xl aspect-square zoom-parent border border-[#FFD1A6] shadow-warm text-left">
              <img src={MARIGOLD_IMG} alt="Marigold" className="absolute inset-0 w-full h-full object-cover zoom-img" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="relative p-5 h-full flex flex-col justify-end text-white">
                <div className="text-[10px] uppercase tracking-widest text-[#FFCF52]">Category</div>
                <div className="font-display text-2xl">Décor</div>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: HeartHandshake, title: "Made by artisans", sub: "Directly sourced" },
            { icon: Truck, title: "Free shipping", sub: "On orders over ₹2000" },
            { icon: ShieldCheck, title: "Secure checkout", sub: "Stripe & Razorpay" },
            { icon: Sparkles, title: "Festive gifting", sub: "Curated & authentic" },
          ].map((t) => (
            <div key={t.title} className="flex items-center gap-4 p-5 bg-white/70 backdrop-blur rounded-2xl border border-[#FFD1A6]">
              <div className="w-11 h-11 rounded-full bg-[#FFF3E0] text-[#E65C00] flex items-center justify-center">
                <t.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="font-body text-sm font-semibold text-[#2C1810]">{t.title}</div>
                <div className="font-body text-xs text-[#5B3D2A]">{t.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-[11px] tracking-[0.25em] uppercase text-[#4A148C] font-body">Handpicked for the season</div>
            <h2 className="font-display text-4xl lg:text-5xl text-[#2C1810] mt-2">Festive Picks</h2>
          </div>
          <Button
            data-testid="home-viewall-btn"
            variant="ghost"
            onClick={() => navigate("/shop")}
            className="rounded-full text-[#4A148C] hover:text-[#E65C00] font-body"
          >
            View all <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {products.length === 0 ? (
          <div className="text-center py-20 bg-white/60 rounded-3xl border border-dashed border-[#FFD1A6]">
            <Sparkles className="w-10 h-10 mx-auto text-[#FFB300] mb-4" />
            <div className="font-display text-2xl text-[#2C1810]">Our storefront is being prepared</div>
            <p className="font-body text-[#5B3D2A] mt-2">Admin can add the first festive products from the panel.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p) => <ProductCard key={p.id} product={p} onAdd={handleAdd} />)}
          </div>
        )}
      </section>

      {/* Story block */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center bg-gradient-to-br from-[#FFF3E0] to-[#FDE9C4] rounded-3xl p-10 lg:p-16 border border-[#FFD1A6]">
          <div>
            <div className="text-[11px] tracking-[0.25em] uppercase text-[#4A148C] font-body mb-4">Our story</div>
            <h3 className="font-display text-4xl text-[#2C1810] leading-tight">
              From the hands of Indian artisans<br /> to your festive table.
            </h3>
            <p className="font-body text-[#5B3D2A] mt-6 leading-relaxed">
              Every diya is hand-painted, every brass piece hammered by third-generation craftsmen, every rangoli motif carries a blessing. Mona&apos;s Creations is a bridge between their workshops and your celebration.
            </p>
            <Button
              data-testid="home-story-cta"
              onClick={() => navigate("/shop")}
              className="mt-8 rounded-full bg-[#4A148C] hover:bg-[#6A2AB0] text-[#FFCF52] px-8 py-6 btn-lift"
            >
              Explore the collection <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={LAMPS_IMG} alt="lamps" className="rounded-2xl aspect-[3/4] object-cover shadow-warm" />
            <img src={MARIGOLD_IMG} alt="marigold" className="rounded-2xl aspect-[3/4] object-cover mt-8 shadow-warm" />
          </div>
        </div>
      </section>
    </div>
  );
}
